import { useEffect, useRef } from 'react';
import type { QuickFillDataScope, QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import type { RequestedParameterIntentInput } from '../../../shared/api/grpc/requestedParameterIntent';
import { perf } from '../../../shared/lib/perfLogger';
import { hasQuickFillDataCache } from '../lib/quickFillDataCache';
import {
  buildCacheReuseProgress,
  buildFetchMetrics,
  buildQuickFillDataScopeSignature,
  buildQuickFillRefreshKey,
  createHostProgressGate,
  mapHostQuickFillLoadProgress,
  sampleNames,
} from '../lib/quickFillInitializationSummary';
import { QUICK_FILL_LOAD_TOTAL_STEPS, type QuickFillLoadProgress } from '../lib/quickFillLoadProgress';
import type { QuickFillFetchResult } from './useQuickFillData';

const QUICK_FILL_REFRESH_DEBOUNCE_MS = 1500;

export type QuickFillRefreshDecision = 'defer' | 'skip' | 'fetch';

export function selectQuickFillRefreshDecision(
  mutationActive: boolean,
  refreshKey: string,
  lastRefreshKey: string
): QuickFillRefreshDecision {
  if (mutationActive) return 'defer';
  return refreshKey === lastRefreshKey ? 'skip' : 'fetch';
}

interface QuickFillRefreshCoordinatorInput {
  mutationActive: boolean;
  initializationDoneRef: React.MutableRefObject<boolean>;
  lastRefreshKeyRef: React.MutableRefObject<string>;
  isInitializing: boolean;
  preferGrpcPayload: boolean;
  snapshotShape: QuickFillSnapshotShapeOption;
  cacheScopeKey?: string | null;
  dataScope?: QuickFillDataScope;
  propertiesSignal: unknown;
  extraPropertiesSignal: unknown;
  buildRequest: () => {
    parameterNames: string[];
    requestedColumns: string[];
    requestedParameters?: RequestedParameterIntentInput[];
  };
  runFetch: (
    parameters: string[] | undefined,
    options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
  ) => Promise<QuickFillFetchResult>;
  setLoadProgress: React.Dispatch<React.SetStateAction<QuickFillLoadProgress>>;
  setRefreshKey: React.Dispatch<React.SetStateAction<string>>;
}

export function useQuickFillRefreshCoordinator(input: QuickFillRefreshCoordinatorInput): void {
  const {
    mutationActive,
    initializationDoneRef,
    lastRefreshKeyRef,
    isInitializing,
    preferGrpcPayload,
    snapshotShape,
    cacheScopeKey,
    dataScope,
    propertiesSignal,
    extraPropertiesSignal,
    buildRequest,
    runFetch,
    setLoadProgress,
    setRefreshKey,
  } = input;
  const terminalReconciliationPendingRef = useRef(false);
  useEffect(() => {
    if (mutationActive && initializationDoneRef.current) {
      terminalReconciliationPendingRef.current = true;
    }
    if (!initializationDoneRef.current || isInitializing) return;
    const refreshKey = buildQuickFillRefreshKey(
      cacheScopeKey,
      snapshotShape,
      buildRequest().parameterNames,
      dataScope
    );
    const forceTerminalReconciliation = !mutationActive &&
      terminalReconciliationPendingRef.current;
    if (!forceTerminalReconciliation && selectQuickFillRefreshDecision(
      mutationActive,
      refreshKey,
      lastRefreshKeyRef.current
    ) !== 'fetch') return;
    if (mutationActive) return;

    let active = true;
    const timer = globalThis.setTimeout(() => {
      if (!active || mutationActive) return;
      terminalReconciliationPendingRef.current = false;
      lastRefreshKeyRef.current = refreshKey;
      setRefreshKey(refreshKey);
      const { parameterNames, requestedColumns } = buildRequest();
      const params = parameterNames.length > 0 ? parameterNames : undefined;
      const cacheAvailable = preferGrpcPayload && hasQuickFillDataCache(params, {
        snapshotShape,
        cacheScopeKey,
        dataScope,
      });
      setLoadProgress(cacheAvailable ? buildCacheReuseProgress({
        detail: `${parameterNames.length} requested Revit parameter${parameterNames.length === 1 ? '' : 's'} already cached`,
        parameterCount: parameterNames.length,
        requestedColumnCount: requestedColumns.length,
      }) : {
        phase: 'requesting',
        message: 'Refreshing Quick Fill data from Revit',
        detail: `${parameterNames.length} requested Revit parameter${parameterNames.length === 1 ? '' : 's'}`,
        currentStep: 2,
        totalSteps: QUICK_FILL_LOAD_TOTAL_STEPS,
        metrics: buildFetchMetrics(null, parameterNames.length, requestedColumns.length),
      });
      perf.log('QF-Init', 'Reconciliation fetch', {
        params: parameterNames.length,
        sampleRequestedParams: sampleNames(parameterNames),
        cacheScopeKey,
        snapshotShape,
        dataScope: buildQuickFillDataScopeSignature(dataScope),
      });
      const shouldPublishHostProgress = createHostProgressGate();
      void runFetch(params, {
        onProgress: (progress) => {
          if (!active || !shouldPublishHostProgress(progress)) return;
          setLoadProgress(mapHostQuickFillLoadProgress(
            progress,
            parameterNames.length,
            requestedColumns.length
          ));
        },
      }).then((fetchResult) => {
        if (!active || fetchResult.source !== 'cache') return;
        setLoadProgress(buildCacheReuseProgress({
          detail: `${fetchResult.familiesCount} families, ${fetchResult.elementsCount} elements, ${fetchResult.availableParametersCount} available parameters`,
          parameterCount: parameterNames.length,
          requestedColumnCount: requestedColumns.length,
          result: fetchResult,
        }));
      });
    }, QUICK_FILL_REFRESH_DEBOUNCE_MS);

    return () => {
      active = false;
      globalThis.clearTimeout(timer);
    };
  }, [mutationActive, isInitializing, preferGrpcPayload, snapshotShape,
    cacheScopeKey, dataScope, buildRequest, runFetch, propertiesSignal, extraPropertiesSignal,
    initializationDoneRef, lastRefreshKeyRef, setLoadProgress, setRefreshKey]);
}
